import { LayerBitMask } from './layers/Layer';

/**
 * The only thing the scheduler needs of a view: "repaint these layers".\
 * Declared structurally rather than as {@link PlaneView} so the coalescing logic
 * stays testable without a WebGL context.
 */
export interface RepaintableView {
    updateView(layers: LayerBitMask): void;
}

/**
 * Collects {@link ViewInvalidator.invalidate} requests and turns a burst of them
 * into **one repaint per view per animation frame**.
 *
 * Each request contributes its {@link LayerBitMask} to the union held for that
 * view; the first request of a burst schedules a `requestAnimationFrame`
 * callback, and that callback repaints every dirty view once with its
 * accumulated union. Culling is not done here but at invalidate time, in
 * {@link ViewManager.invalidate}: the culling volume is live data, so it has to
 * be evaluated when the caller reports the change rather than a frame later.
 *
 * Only the timing of a repaint is affected, never its content — deferred by at
 * most one frame, to the same set of views, with the same layer mask each would
 * have drawn.
 *
 * A hidden tab gets no animation frames, so repaints pile up there — but only as
 * one entry per view, holding a union, so the pending state stays bounded however
 * long the tab is hidden, and the first frame after it is shown again repaints
 * from the state as it then is.
 */
export class ViewInvalidationScheduler {
    /**
     * Union of the layer masks each view has been invalidated with since the
     * last flush. Keyed by view instance, so a view that is removed simply
     * stops being reachable rather than leaving an index to fix up.
     */
    private readonly dirtyLayers = new Map<RepaintableView, LayerBitMask>();

    /** Handle of the scheduled flush, or undefined when none is pending. */
    private pendingFrame: number | undefined = undefined;

    /**
     * @param activeViews Supplies the views that currently exist. Consulted at
     * flush time so a view deleted after being marked dirty is never repainted
     * — its GL resources are gone by then.
     */
    public constructor(
        private readonly activeViews: () => Iterable<RepaintableView>
    ) {}

    /**
     * Record that {@link view} has to repaint {@link layers}, and make sure a
     * flush is scheduled.
     * @param view The view to repaint.
     * @param layers Bitflag of the layers that changed in that view.
     */
    public markDirty(view: RepaintableView, layers: LayerBitMask): void {
        this.dirtyLayers.set(view, (this.dirtyLayers.get(view) ?? 0) | layers);
        if (this.pendingFrame === undefined) {
            this.pendingFrame = requestAnimationFrame(() => {
                this.pendingFrame = undefined;
                this.flush();
            });
        }
    }

    /**
     * Whether {@link view} is already scheduled to repaint at least
     * {@link layers} this frame, so that marking it again could not change what
     * it ends up drawing.
     *
     * Lets a caller skip work whose only purpose is to decide whether to call
     * {@link markDirty} — see {@link ViewManager.invalidate}, which skips the
     * culling intersection on this.
     * @param view The view to test.
     * @param layers Bitflag the caller is about to mark dirty.
     */
    public isDirtyWith(view: RepaintableView, layers: LayerBitMask): boolean {
        const pending = this.dirtyLayers.get(view);
        return pending !== undefined && (pending & layers) === layers;
    }

    /**
     * Drop any pending repaint for {@link view}, for use when the view is being
     * removed or has just been repainted by other means. The flush-time
     * {@link activeViews} guard covers the removal case too; discarding there
     * simply stops a dead view from being retained until the next flush.
     *
     * Cancels the scheduled frame once no view remains dirty, so a frame
     * scheduled for a mask that discarding just emptied out doesn't fire
     * to repaint nothing.
     * @param view The view to drop.
     */
    public discard(view: RepaintableView): void {
        this.dirtyLayers.delete(view);
        if (this.dirtyLayers.size === 0) {
            this.cancelPendingFrame();
        }
    }

    /**
     * Run the pending repaints now and cancel the scheduled frame.
     *
     * The dirty state is cleared *before* the repaints run, so an invalidation
     * raised from inside a repaint schedules a new frame instead of being
     * dropped or extending this flush indefinitely.
     */
    public flush(): void {
        this.cancelPendingFrame();
        if (this.dirtyLayers.size === 0) {
            return;
        }
        const dirty = [...this.dirtyLayers];
        this.dirtyLayers.clear();
        const active = new Set(this.activeViews());
        for (const [view, layers] of dirty) {
            if (active.has(view)) {
                view.updateView(layers);
            }
        }
    }

    /**
     * Drop all pending repaints without running them. For callers that have
     * just repainted everything synchronously (see {@link ViewManager.resetViews})
     * and for teardown.
     */
    public reset(): void {
        this.dirtyLayers.clear();
        this.cancelPendingFrame();
    }

    private cancelPendingFrame(): void {
        if (this.pendingFrame !== undefined) {
            cancelAnimationFrame(this.pendingFrame);
            this.pendingFrame = undefined;
        }
    }
}
